import { useEffect, useMemo, useState } from 'react';
import type { CycleRing } from '@/lib/cycleTypes';
import { cn } from '@/lib/utils';

const ringColorFallbacks: Record<string, string> = {
  lunar: 'hsl(220, 60%, 70%)',
  moons: 'hsl(280, 60%, 60%)',
  days: 'hsl(45, 80%, 60%)',
  seals: 'hsl(0, 70%, 60%)',
  colors: 'hsl(160, 60%, 50%)',
  plasmas: 'hsl(320, 70%, 60%)',
};

const getStableRingFallback = (ringId: string) => {
  const mapped = ringColorFallbacks[ringId];
  if (mapped) return mapped;
  const seed = ringId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return `hsl(${(seed * 53) % 360},60%,60%)`;
};

const getRingColor = (ring: CycleRing, _index: number) => {
  const explicit = ring.colors?.[ring.currentStage ?? 0] ?? ring.colors?.[0];
  if (explicit) return `hsl(${explicit})`;
  return getStableRingFallback(ring.id);
};

interface ShortCyclesWidgetProps {
  rings: CycleRing[];
  hiddenRings?: string[];
  currentDayOfYear: number;
  onSyncToNow?: () => void;
  className?: string;
}

export function ShortCyclesWidget({
  rings,
  hiddenRings = [],
  currentDayOfYear,
  onSyncToNow,
  className,
}: ShortCyclesWidgetProps) {
  const visibleRings = useMemo(
    () => rings.filter((r) => r && r.id && !hiddenRings.includes(r.id)),
    [rings, hiddenRings],
  );

  const [activeRingIndex, setActiveRingIndex] = useState(0);

  useEffect(() => {
    if (visibleRings.length === 0) { setActiveRingIndex(0); return; }
    if (activeRingIndex > visibleRings.length - 1) setActiveRingIndex(0);
  }, [activeRingIndex, visibleRings.length]);

  const activeRing = visibleRings[activeRingIndex] ?? null;
  const activeRingColor = activeRing ? getRingColor(activeRing, activeRingIndex) : 'hsl(220,60%,70%)';
  const activeStage = activeRing?.currentStage ?? 0;
  const activeStages = Math.max(1, activeRing?.stages ?? 1);
  const activeLabel = activeRing?.labels?.[activeStage] ?? String(activeStage + 1);
  const activeProgress = (activeStage + 1) / activeStages;

  return (
    <div className={cn('relative w-full h-full flex flex-col gap-2 px-3 py-2', className)}>

      {/* Always-visible horizontal ring tabs */}
      {visibleRings.length > 0 && (
        <div className="flex flex-row gap-1 flex-wrap">
          {visibleRings.map((ring, index) => {
            const isActive = index === activeRingIndex;
            const dotColor = getRingColor(ring, index);
            return (
              <button
                key={ring.id}
                type="button"
                onClick={() => setActiveRingIndex(index)}
                className={cn(
                  'inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-[9px] font-medium uppercase tracking-[0.1em] transition-all',
                  isActive
                    ? 'border-white/20 text-slate-950 shadow-md'
                    : 'border-white/15 bg-white/[0.05] text-white/60 active:bg-white/10',
                )}
                style={{ background: isActive ? dotColor : undefined }}
                aria-label={`Show ${ring.name ?? ring.id} ring`}
              >
                {(ring.name ?? ring.id).slice(0, 10)}
              </button>
            );
          })}
          <span className="ml-auto text-[9px] text-amber-300/50 tabular-nums self-center">Day {currentDayOfYear}</span>
        </div>
      )}

      {/* Active ring detail */}
      <div className="flex-1 min-h-0 flex flex-col justify-center">
        {activeRing ? (
          <div
            className="rounded-2xl border px-3 py-3 flex flex-col gap-2"
            style={{
              borderColor: `${activeRingColor}40`,
              background: `linear-gradient(180deg, ${activeRingColor}18 0%, rgba(10,16,34,0.34) 100%)`,
            }}
          >
            <div className="flex items-end justify-between gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/45">Current Stage</div>
                <div className="text-lg font-semibold leading-none" style={{ color: activeRingColor }}>
                  {activeLabel}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/45">Progress</div>
                <div className="text-sm font-medium text-white/80">
                  {activeStage + 1}/{activeStages}
                </div>
              </div>
            </div>

            <div className="relative h-5 overflow-hidden rounded-full bg-slate-950/45">
              <div className="absolute inset-0 flex">
                {Array.from({ length: activeStages }, (_, i) => {
                  const isFilled = i <= activeStage;
                  const isCurrent = i === activeStage;
                  return (
                    <div
                      key={i}
                      className="flex-1 border-r border-slate-950/30 last:border-r-0 transition-all"
                      style={{
                        background: isFilled ? activeRingColor : 'transparent',
                        opacity: isCurrent ? 1 : isFilled ? 0.44 : 0.12,
                        boxShadow: isCurrent ? `0 0 14px ${activeRingColor}` : 'none',
                      }}
                    />
                  );
                })}
              </div>
            </div>

            {onSyncToNow && (
              <button
                onClick={onSyncToNow}
                className="self-end text-[9px] text-amber-300/70 active:text-amber-200 transition-colors"
              >
                Sync now
              </button>
            )}
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-[11px] text-amber-200/55">
            No visible cycles
          </div>
        )}
      </div>
    </div>
  );
}
